import React, { useState, useEffect } from 'react';
import { calculatePayGrade } from '../utils/scoreCalculator';
import { loadTeacherNames } from '../utils/teacherData';
import * as XLSX from 'xlsx';
import Header from './ui/Header';
import Card from './ui/Card';
import Button from './ui/Button';
import Badge from './ui/Badge';
import Modal from './ui/Modal';

const AdminTool = () => {
  const [teachers, setTeachers] = useState([]);
  const [allTeacherNames, setAllTeacherNames] = useState([]);
  const [newTeacherName, setNewTeacherName] = useState('');
  const [showAddNew, setShowAddNew] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Load teacher names on mount
  useEffect(() => {
    const loadNames = async () => {
      const names = await loadTeacherNames();
      setAllTeacherNames(names);

      // Initialize teachers with all names and empty scores
      const initialTeachers = names.map((teacher) => ({
        name: teacher.name,
        isDual: teacher.isDual,
        qualification: '',
        experience: '',
        englishLevel: '',
        competencies: '',
        totalScore: 0,
        payRate: '-',
      }));
      setTeachers(initialTeachers);
    };

    loadNames();
  }, []);

  const updateTeacherScore = (index, field, value) => {
    const updated = [...teachers];
    updated[index][field] = value;

    // Recalculate if all scores are present
    if (
      updated[index].qualification !== '' &&
      updated[index].experience !== '' &&
      updated[index].englishLevel !== '' &&
      updated[index].competencies !== ''
    ) {
      const { totalScore, payRate } = calculatePayGrade(
        updated[index].qualification,
        updated[index].experience,
        updated[index].englishLevel,
        updated[index].competencies
      );
      updated[index].totalScore = totalScore;
      updated[index].payRate = payRate;
    } else {
      updated[index].totalScore = 0;
      updated[index].payRate = '-';
    }

    setTeachers(updated);
  };

  const addNewTeacher = () => {
    if (newTeacherName.trim()) {
      const newTeacher = {
        name: newTeacherName,
        isDual: false,
        qualification: '',
        experience: '',
        englishLevel: '',
        competencies: '',
        totalScore: 0,
        payRate: '-',
      };
      setTeachers([...teachers, newTeacher]);
      setNewTeacherName('');
      setShowAddNew(false);
    }
  };

  const deleteTeacher = (index) => {
    setTeachers(teachers.filter((_, i) => i !== index));
  };

  const exportToExcel = () => {
    const filledTeachers = teachers.filter(
      (t) =>
        t.qualification !== '' ||
        t.experience !== '' ||
        t.englishLevel !== '' ||
        t.competencies !== ''
    );

    if (filledTeachers.length === 0) {
      alert('No teachers with scores to export');
      return;
    }

    const exportData = filledTeachers.map((t) => ({
      'Teacher Name': t.name,
      'Qualification': t.qualification || '',
      'Experience': t.experience || '',
      'English Level': t.englishLevel || '',
      'Competencies': t.competencies || '',
      'Total Score': t.totalScore,
      'Pay Rate': t.payRate,
    }));

    const gradeCount = filledTeachers.reduce(
      (acc, t) => {
        acc[t.payRate] = (acc[t.payRate] || 0) + 1;
        return acc;
      },
      {}
    );

    exportData.push({});
    exportData.push({ 'Teacher Name': 'Summary' });
    exportData.push({ 'Teacher Name': 'Total Teachers', 'Qualification': filledTeachers.length });
    Object.keys(gradeCount).forEach((grade) => {
      exportData.push({
        'Teacher Name': `Grade ${grade}`,
        'Qualification': gradeCount[grade],
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Teacher Scores');

    worksheet['!cols'] = [
      { wch: 20 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
      { wch: 14 },
      { wch: 12 },
      { wch: 10 },
    ];

    const date = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `batch_teacher_scores_${date}.xlsx`);
  };

  const saveToLocalStorage = () => {
    const filledTeachers = teachers.filter(
      (t) =>
        t.qualification !== '' ||
        t.experience !== '' ||
        t.englishLevel !== '' ||
        t.competencies !== ''
    );

    if (filledTeachers.length === 0) {
      alert('No teachers with scores to save');
      return;
    }

    const batch = {
      timestamp: new Date().toISOString(),
      teachers: filledTeachers,
    };

    const key = `lvcalc_batch_${Date.now()}`;
    localStorage.setItem(key, JSON.stringify(batch));
    alert(`Batch saved: ${batch.timestamp}`);
  };

  const loadFromLocalStorage = () => {
    const keys = Object.keys(localStorage).filter((k) => k.startsWith('lvcalc_batch_'));

    if (keys.length === 0) {
      alert('No saved batches found');
      return;
    }

    const savedKey = keys[keys.length - 1];
    const savedBatch = JSON.parse(localStorage.getItem(savedKey));

    const updated = [...teachers];
    savedBatch.teachers.forEach((saved) => {
      const index = updated.findIndex((t) => t.name === saved.name);
      if (index >= 0) {
        updated[index] = { ...updated[index], ...saved };
      } else {
        updated.push(saved);
      }
    });

    setTeachers(updated);
    alert(`Batch loaded: ${new Date(savedBatch.timestamp).toLocaleString()}`);
  };

  const exportToCSV = () => {
    const filledTeachers = teachers.filter(
      (t) =>
        t.qualification !== '' ||
        t.experience !== '' ||
        t.englishLevel !== '' ||
        t.competencies !== ''
    );

    if (filledTeachers.length === 0) {
      alert('No teachers with scores to export');
      return;
    }

    let csv = 'Teacher Name,Qualification,Experience,English Level,Competencies,Total Score,Pay Rate\n';

    filledTeachers.forEach((t) => {
      csv += `"${t.name}",${t.qualification || ''},${t.experience || ''},${t.englishLevel || ''},${t.competencies || ''},${t.totalScore},${t.payRate}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().split('T')[0];
    link.download = `batch_teacher_scores_${date}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const importFromCSV = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target?.result;
        const lines = csv.split('\n');

        const imported = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          const matches = line.match(/"([^"]*)"|([^,]+)/g);
          if (!matches || matches.length < 2) continue;

          const name = matches[0].replace(/"/g, '').trim();
          const qualification = matches[1]?.trim() || '';
          const experience = matches[2]?.trim() || '';
          const englishLevel = matches[3]?.trim() || '';
          const competencies = matches[4]?.trim() || '';

          if (name) {
            const { totalScore, payRate } = calculatePayGrade(
              qualification || 0,
              experience || 0,
              englishLevel || 0,
              competencies || 0
            );

            imported.push({
              name,
              isDual: false,
              qualification: qualification === '' ? '' : qualification,
              experience: experience === '' ? '' : experience,
              englishLevel: englishLevel === '' ? '' : englishLevel,
              competencies: competencies === '' ? '' : competencies,
              totalScore,
              payRate,
            });
          }
        }

        if (imported.length === 0) {
          alert('No valid data found in CSV');
          return;
        }

        const updated = [...teachers];
        imported.forEach((importedTeacher) => {
          const index = updated.findIndex((t) => t.name === importedTeacher.name);
          if (index >= 0) {
            updated[index] = { ...updated[index], ...importedTeacher };
          } else {
            updated.push(importedTeacher);
          }
        });

        setTeachers(updated);
        alert(`Imported ${imported.length} teacher(s)`);
        event.target.value = '';
      } catch (error) {
        alert('Error importing CSV: ' + error.message);
      }
    };

    reader.readAsText(file);
  };

  const getGradeBadgeVariant = (grade) => {
    switch (grade) {
      case 'B':
        return 'secondary';
      case 'C':
        return 'purple';
      case 'D':
        return 'success';
      default:
        return 'info';
    }
  };

  const filteredTeachers = teachers.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filledCount = teachers.filter(
    (t) =>
      t.qualification !== '' ||
      t.experience !== '' ||
      t.englishLevel !== '' ||
      t.competencies !== ''
  ).length;

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <Header
        title="Teacher Management"
        subtitle={`Managing ${filledCount} teacher${filledCount !== 1 ? 's' : ''} with completed evaluations`}
      >
        <Button variant="primary" onClick={() => setShowAddNew(true)}>
          Add Teacher
        </Button>
      </Header>

      {/* Action Bar */}
      <Card className="mb-6">
        <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="secondary" onClick={exportToExcel}>
            Export (Excel)
          </Button>
          <Button variant="secondary" onClick={exportToCSV}>
            Export (CSV)
          </Button>
          <label style={{ cursor: 'pointer', margin: 0 }}>
            <Button variant="secondary" as="span">
              Import (CSV)
            </Button>
            <input
              type="file"
              accept=".csv"
              onChange={importFromCSV}
              style={{ display: 'none' }}
            />
          </label>
          <div style={{ flex: 1 }}></div>
          <Button variant="secondary" onClick={saveToLocalStorage}>
            Save
          </Button>
          <Button variant="secondary" onClick={loadFromLocalStorage}>
            Load
          </Button>
          <Button variant="danger" onClick={() => {
            if (window.confirm('Clear all teacher data?')) {
              setTeachers(allTeacherNames.map((t) => ({
                name: t.name,
                isDual: t.isDual,
                qualification: '',
                experience: '',
                englishLevel: '',
                competencies: '',
                totalScore: 0,
                payRate: '-',
              })));
            }
          }}>
            Reset All
          </Button>
        </div>
      </Card>

      {/* Search Bar */}
      <Card className="mb-6">
        <input
          type="text"
          placeholder="Search teachers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            display: 'block',
            width: '100%',
            padding: 'var(--spacing-sm) var(--spacing-md)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--font-size-body)',
          }}
        />
      </Card>

      {/* Teachers Table */}
      <Card style={{ overflow: 'hidden' }} className="mb-6">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' }}>
                <th style={{ padding: 'var(--spacing-md)', textAlign: 'left', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Teacher Name</th>
                <th style={{ padding: 'var(--spacing-md)', textAlign: 'center', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Qualification</th>
                <th style={{ padding: 'var(--spacing-md)', textAlign: 'center', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Experience</th>
                <th style={{ padding: 'var(--spacing-md)', textAlign: 'center', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>English Level</th>
                <th style={{ padding: 'var(--spacing-md)', textAlign: 'center', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Competencies</th>
                <th style={{ padding: 'var(--spacing-md)', textAlign: 'center', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Total Score</th>
                <th style={{ padding: 'var(--spacing-md)', textAlign: 'center', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Pay Rate</th>
                <th style={{ padding: 'var(--spacing-md)', textAlign: 'center', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTeachers.length > 0 ? (
                filteredTeachers.map((teacher, index) => (
                  <tr key={index} style={{
                    backgroundColor: index % 2 === 0 ? 'var(--color-bg-light)' : 'var(--color-bg-surface)',
                    borderBottom: '1px solid var(--color-border)',
                    transition: 'background-color var(--transition-base)',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-subtle)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'var(--color-bg-light)' : 'var(--color-bg-surface)'}
                  >
                    <td style={{ padding: 'var(--spacing-md)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>
                      {teacher.name}
                    </td>
                    <td style={{ padding: 'var(--spacing-md)', textAlign: 'center' }}>
                      <input
                        type="number"
                        min="0"
                        max="4"
                        value={teacher.qualification}
                        onChange={(e) =>
                          updateTeacherScore(index, 'qualification', e.target.value === '' ? '' : Math.max(0, Math.min(4, parseInt(e.target.value))))
                        }
                        style={{
                          width: '100%',
                          padding: 'var(--spacing-xs) var(--spacing-sm)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-lg)',
                          textAlign: 'center',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 600,
                        }}
                      />
                    </td>
                    <td style={{ padding: 'var(--spacing-md)', textAlign: 'center' }}>
                      <input
                        type="number"
                        min="0"
                        max="4"
                        value={teacher.experience}
                        onChange={(e) =>
                          updateTeacherScore(index, 'experience', e.target.value === '' ? '' : Math.max(0, Math.min(4, parseInt(e.target.value))))
                        }
                        style={{
                          width: '100%',
                          padding: 'var(--spacing-xs) var(--spacing-sm)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-lg)',
                          textAlign: 'center',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 600,
                        }}
                      />
                    </td>
                    <td style={{ padding: 'var(--spacing-md)', textAlign: 'center' }}>
                      <input
                        type="number"
                        min="0"
                        max="4"
                        value={teacher.englishLevel}
                        onChange={(e) =>
                          updateTeacherScore(index, 'englishLevel', e.target.value === '' ? '' : Math.max(0, Math.min(4, parseInt(e.target.value))))
                        }
                        style={{
                          width: '100%',
                          padding: 'var(--spacing-xs) var(--spacing-sm)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-lg)',
                          textAlign: 'center',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 600,
                        }}
                      />
                    </td>
                    <td style={{ padding: 'var(--spacing-md)', textAlign: 'center' }}>
                      <input
                        type="number"
                        min="0"
                        max="4"
                        value={teacher.competencies}
                        onChange={(e) =>
                          updateTeacherScore(index, 'competencies', e.target.value === '' ? '' : Math.max(0, Math.min(4, parseInt(e.target.value))))
                        }
                        style={{
                          width: '100%',
                          padding: 'var(--spacing-xs) var(--spacing-sm)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-lg)',
                          textAlign: 'center',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 600,
                        }}
                      />
                    </td>
                    <td style={{ padding: 'var(--spacing-md)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
                      {teacher.totalScore}
                    </td>
                    <td style={{ padding: 'var(--spacing-md)', textAlign: 'center' }}>
                      {teacher.payRate !== '-' ? (
                        <Badge variant={getGradeBadgeVariant(teacher.payRate)}>
                          Grade {teacher.payRate}
                        </Badge>
                      ) : (
                        <span style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--font-size-sm)' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: 'var(--spacing-md)', textAlign: 'center' }}>
                      <button
                        onClick={() => deleteTeacher(index)}
                        style={{
                          padding: 'var(--spacing-xs) var(--spacing-sm)',
                          border: 'none',
                          borderRadius: 'var(--radius-lg)',
                          backgroundColor: '#EF4444',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 600,
                          transition: 'background-color var(--transition-base)',
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#DC2626'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#EF4444'}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{ padding: 'var(--spacing-lg)', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
                    {searchQuery ? 'No teachers found matching your search' : 'No teachers available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add New Teacher Modal */}
      <Modal isOpen={showAddNew} onClose={() => setShowAddNew(false)} title="Add New Teacher">
        <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
          <input
            type="text"
            placeholder="Enter teacher name"
            value={newTeacherName}
            onChange={(e) => setNewTeacherName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addNewTeacher()}
            autoFocus
            style={{
              flex: 1,
              padding: 'var(--spacing-sm) var(--spacing-md)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--font-size-body)',
            }}
          />
          <Button variant="primary" onClick={addNewTeacher}>
            Add
          </Button>
          <Button variant="secondary" onClick={() => setShowAddNew(false)}>
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminTool;
